import { SearchInput } from "@freightsimple/ui-core/Controls/SearchInput";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import { useMemo, useState } from "react";
import { filterSavedCommodities } from "@/Shared/LineItems/Helpers/filterSavedCommodities";

const MAX_RESULTS = 50;

interface SavedCommodityPickerProps {
  savedCommodities: SavedCommodity[];
  onSelectSavedCommodity: (savedCommodity: SavedCommodity) => void;
  onSomethingElse: () => void;
  heading?: string | undefined;
  listHeight?: number | string | undefined;
}

function formatDimensions(sc: SavedCommodity): string | undefined {
  if (
    sc.lengthInches === undefined ||
    sc.widthInches === undefined ||
    sc.heightInches === undefined
  ) {
    return undefined;
  }
  return `${sc.lengthInches} x ${sc.widthInches} x ${sc.heightInches} in`;
}

function metadataSegments(sc: SavedCommodity): string[] {
  const segments: string[] = [];
  if (sc.skuIdentifier) {
    segments.push(`SKU ${sc.skuIdentifier}`);
  }
  if (sc.nmfcItemNumber) {
    segments.push(`NMFC ${sc.nmfcItemNumber}`);
  }
  if (sc.dangerousUnNumber) {
    segments.push(sc.dangerousUnNumber);
  }
  const dims = formatDimensions(sc);
  if (dims) {
    segments.push(dims);
  }
  return segments;
}

interface PickerRowProps {
  primary: string;
  subtitle?: string | undefined;
  testId: string;
  onClick: () => void;
}

function PickerRow_(props: PickerRowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={props.testId}
      onClick={props.onClick}
      onKeyDown={function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          props.onClick();
        }
      }}
      className="group w-full cursor-pointer rounded-[3px] px-3 py-2 hover:bg-muted"
    >
      <div className="text-base leading-tight font-bold text-brand-text group-hover:underline">
        {props.primary}
      </div>
      {props.subtitle && (
        <div className="mt-0.5 text-xs leading-snug font-normal text-content-muted">
          {props.subtitle}
        </div>
      )}
    </div>
  );
}

export function BookShipmentSavedCommodityPicker(
  props: SavedCommodityPickerProps,
) {
  const {
    savedCommodities,
    onSelectSavedCommodity,
    onSomethingElse,
    heading = "What would you like to ship?",
    listHeight,
  } = props;

  // A caller that fixes the height gets a list that always occupies it, so a
  // dialog doesn't resize as the customer types. Otherwise the list grows to a
  // cap and then scrolls.
  const fixedHeight =
    listHeight === undefined
      ? undefined
      : typeof listHeight === "number"
        ? `${listHeight}px`
        : listHeight;

  const [filter, setFilter] = useState("");

  const filtered = useMemo(
    function () {
      return filterSavedCommodities(savedCommodities, filter);
    },
    [savedCommodities, filter],
  );

  const visible = filtered.slice(0, MAX_RESULTS);
  const truncatedCount = Math.max(0, filtered.length - MAX_RESULTS);

  return (
    <Stack gap={4}>
      <T size="base" weight="bold">
        {heading}
      </T>
      <SearchInput
        value={filter}
        onChange={setFilter}
        placeholder="Search by description, SKU, NMFC, or UN number"
        width="full"
        data-testid="saved-commodity-picker-search"
      />
      {visible.length === 0 ? (
        <div
          data-testid="saved-commodity-no-results"
          className="px-1 py-4 text-sm text-content-muted"
          {...(fixedHeight !== undefined && { style: { height: fixedHeight } })}
        >
          {filter.trim().length > 0 ? (
            <>No commodities match &ldquo;{filter}&rdquo;</>
          ) : (
            "No saved commodities"
          )}
        </div>
      ) : (
        <div
          data-testid="saved-commodity-list"
          className="flex w-full flex-col items-start overflow-y-auto"
          style={
            fixedHeight !== undefined
              ? { height: fixedHeight }
              : { maxHeight: "320px" }
          }
        >
          {visible.map(function (sc) {
            const segments = metadataSegments(sc);
            return (
              <PickerRow_
                key={sc.savedCommodityId}
                testId={`saved-commodity-option-${sc.savedCommodityId}`}
                primary={sc.description}
                subtitle={
                  segments.length > 0 ? segments.join(" · ") : undefined
                }
                onClick={function () {
                  onSelectSavedCommodity(sc);
                }}
              />
            );
          })}
        </div>
      )}
      {/* A caller with a fixed list height reserves the hint's line even when
          there's no hint, so the panel doesn't jump as the customer types their
          way past the cap and back. */}
      <div {...(listHeight !== undefined && { className: "min-h-[26px]" })}>
        {truncatedCount > 0 && (
          <div
            data-testid="saved-commodity-truncation-hint"
            className="px-1 text-xs text-content-muted italic"
          >
            +{truncatedCount} more — refine your search
          </div>
        )}
      </div>
      <div className="w-full border-t border-border pt-3">
        <PickerRow_
          testId="saved-commodity-something-else"
          primary="Something else"
          subtitle="Enter custom commodity details"
          onClick={onSomethingElse}
        />
      </div>
    </Stack>
  );
}
